// SteamSmoke
// Features:
// - Animated plane with Perlin noise for steam/smoke effects
// - Configurable twist, scale, and time offset

import { useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import { useTexture } from '@react-three/drei'
import * as THREE from 'three'

import vertexShader from '../shaders/steamSmoke/vertex.glsl'
import fragmentShader from '../shaders/steamSmoke/fragment.glsl'

import perlinTextureURL from '/textures/perlin.png'

export default function SteamSmoke({
    position = [0, 0, 0],
    rotation = [0, 0, 0],
    width = 0.6,
    height = 1.2,
    opacity = 1,
    textureScale = 1,
    timeOffset = 0,
    twist = 0,
}) {
    // Geometry
    const geometry = useMemo(
        () => new THREE.PlaneGeometry(width, height, 20, 40),
        [width, height]
    )

    // Textures
    const perlinTexture = useTexture(perlinTextureURL, (t) => {
        t.colorSpace = THREE.NoColorSpace
        t.wrapS = THREE.RepeatWrapping
        t.wrapT = THREE.RepeatWrapping
        t.minFilter = THREE.LinearFilter
        t.magFilter = THREE.LinearFilter
    })

    // Material
    const material = useMemo(() => {
        return new THREE.ShaderMaterial({
            uniforms: {
                uTime: { value: 0 },
                uPerlinTexture: { value: perlinTexture },
                uOpacity: { value: opacity },
                uTextureScale: { value: textureScale },
                uTwist: { value: twist },
            },
            vertexShader,
            fragmentShader,
            transparent: true,
            depthWrite: false,
            blending: THREE.NormalBlending,
            side: THREE.DoubleSide,
        })
    }, [perlinTexture, opacity, textureScale, twist])

    // Animation
    useFrame((state) => {
        if (material) {
            material.uniforms.uTime.value = state.clock.elapsedTime + timeOffset
        }
    })

    return (
        <mesh
            geometry={geometry}
            material={material}
            position={position}
            rotation={rotation}
        />
    )
}
